import { useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import { POWERBI_REPORTS } from '@/config/powerbiReports'
import { useAdminUsersList } from '@/hooks/useAdminUsers'
import { useCreateReportPermission } from '@/hooks/useReportPermissions'
import type { ReportType } from '@/api/reportPermissions'

interface ReportOption {
  reportId: string
  reportType: ReportType
  label: string
}

const ALL_REPORT_OPTIONS: ReportOption[] = POWERBI_REPORTS.IFP.map(r => ({
  reportId: r.reportId,
  reportType: 'IFP' as const,
  label: r.label,
}))

interface CreateAssignmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateAssignmentDialog({
  open,
  onOpenChange,
}: CreateAssignmentDialogProps) {
  const [userSearch, setUserSearch] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [selectedUserLabel, setSelectedUserLabel] = useState<string | null>(
    null
  )
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null)

  const { data: usersData, isLoading: isLoadingUsers } = useAdminUsersList({
    search: userSearch,
    role: 'all',
    page: 1,
    pageSize: 50,
  })

  const userOptions = useMemo(
    () =>
      (usersData?.data ?? []).map(u => ({
        value: u.id,
        label: u.display_name ? `${u.display_name} (${u.email})` : u.email,
      })),
    [usersData]
  )

  const createMutation = useCreateReportPermission()

  const selectedReport = ALL_REPORT_OPTIONS.find(
    r => r.reportId === selectedReportId
  )

  const reset = () => {
    setUserSearch('')
    setSelectedUserId(null)
    setSelectedUserLabel(null)
    setSelectedReportId(null)
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  const handleSubmit = () => {
    if (!selectedUserId || !selectedReport) return
    createMutation.mutate(
      {
        user_id: selectedUserId,
        report_id: selectedReport.reportId,
        report_type: selectedReport.reportType,
      },
      {
        onSuccess: () => {
          reset()
          onOpenChange(false)
        },
      }
    )
  }

  const canSubmit =
    selectedUserId !== null &&
    selectedReportId !== null &&
    !createMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Grant report access</DialogTitle>
          <DialogDescription>
            Pick a user and a report. The user will be able to view that report
            on their dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">User</label>
            <Combobox
              options={userOptions}
              value={selectedUserId}
              onValueChange={(value, option) => {
                setSelectedUserId(value)
                setSelectedUserLabel(value ? (option?.label ?? null) : null)
              }}
              selectedLabel={selectedUserLabel}
              placeholder="Search users..."
              isLoading={isLoadingUsers}
              onSearchChange={setUserSearch}
              filterOptions={false}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">
              Report
            </label>
            <Select
              value={selectedReportId ?? undefined}
              onValueChange={value => setSelectedReportId(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a report" />
              </SelectTrigger>
              <SelectContent>
                {ALL_REPORT_OPTIONS.map(r => (
                  <SelectItem key={r.reportId} value={r.reportId}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Grant access'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
